import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import { apiGet } from '../services/apiClient'

const FirebaseUsers = () => {
  const { userClaims } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('email')
  const [sortDirection, setSortDirection] = useState('asc')

  if (!userClaims?.sadmin) {
    return <Navigate to="/devices" replace />
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await apiGet('/api/ulist_with_claims')
      const userList = Array.isArray(data) ? data : (data.users || [])
      setUsers(userList)
    } catch (err) {
      console.error('Error fetching Firebase users:', err)
      setError(err.message || 'Failed to load Firebase users')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return '-'
    try {
      const date = new Date(timestamp)
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return '-'
    }
  }

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      (user.email || '').toLowerCase().includes(term) ||
      (user.name || '').toLowerCase().includes(term) ||
      (user.uid || '').toLowerCase().includes(term) ||
      (user.claim?.customer || '').toLowerCase().includes(term)
    )
  })

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = '', bVal = ''
    if (sortField === 'email') {
      aVal = (a.email || '').toLowerCase()
      bVal = (b.email || '').toLowerCase()
    } else if (sortField === 'name') {
      aVal = (a.name || '').toLowerCase()
      bVal = (b.name || '').toLowerCase()
    } else if (sortField === 'createdOn') {
      aVal = a.createdOn || 0
      bVal = b.createdOn || 0
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <i className="fas fa-sort text-muted ms-1" style={{ fontSize: '0.75rem' }}></i>
    return sortDirection === 'asc'
      ? <i className="fas fa-sort-up ms-1" style={{ fontSize: '0.75rem' }}></i>
      : <i className="fas fa-sort-down ms-1" style={{ fontSize: '0.75rem' }}></i>
  }

  const renderClaimBadges = (claim) => {
    if (!claim || typeof claim !== 'object') return <span className="text-muted">-</span>
    const badges = []
    if (claim.sadmin) badges.push(<span key="sa" className="badge bg-danger me-1">sadmin</span>)
    if (claim.customer) badges.push(<span key="cust" className="badge bg-info me-1">{claim.customer}</span>)
    if (claim.cc) badges.push(<span key="cc" className="badge bg-secondary me-1">CC</span>)
    if (claim.layout) badges.push(<span key="lay" className="badge bg-secondary me-1">Layout</span>)
    return badges.length > 0 ? badges : <span className="text-muted">No claims</span>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="fas fa-users-cog me-2"></i>
          Firebase Users
          {!loading && (
            <span className="badge bg-primary ms-2" style={{ fontSize: '0.6em', verticalAlign: 'middle' }}>
              {users.length}
            </span>
          )}
        </h4>
        <button className="btn btn-outline-primary btn-sm" onClick={fetchUsers} disabled={loading}>
          <i className={`fas fa-sync-alt me-1 ${loading ? 'fa-spin' : ''}`}></i>
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button className="btn btn-sm btn-outline-danger ms-auto" onClick={fetchUsers}>
            Retry
          </button>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <div className="row align-items-center">
            <div className="col">
              <div className="input-group input-group-sm" style={{ maxWidth: '350px' }}>
                <span className="input-group-text bg-white">
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by email, name, UID, customer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="btn btn-outline-secondary" onClick={() => setSearchTerm('')}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-auto text-muted small">
              {searchTerm ? `${filteredUsers.length} of ${users.length} users` : `${users.length} users`}
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2 mb-0">Loading Firebase users...</p>
            </div>
          ) : sortedUsers.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-users text-muted mb-3" style={{ fontSize: '2rem' }}></i>
              <p className="text-muted mb-0">
                {searchTerm ? 'No users match your search' : 'No users found'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '40px' }} className="text-center">#</th>
                    <th role="button" onClick={() => handleSort('email')}>
                      Email <SortIcon field="email" />
                    </th>
                    <th role="button" onClick={() => handleSort('name')}>
                      Name <SortIcon field="name" />
                    </th>
                    <th>Claims</th>
                    <th className="text-center">Verified</th>
                    <th className="text-center">Status</th>
                    <th role="button" onClick={() => handleSort('createdOn')}>
                      Created <SortIcon field="createdOn" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user, index) => (
                    <tr key={user.uid || index}>
                      <td className="text-center text-muted small">{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-2"
                            style={{ width: '32px', height: '32px', minWidth: '32px' }}>
                            <i className="fas fa-user text-primary" style={{ fontSize: '0.8rem' }}></i>
                          </div>
                          <div>
                            <div className="small fw-medium">{user.email || '-'}</div>
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>{user.uid || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="small">{user.name || '-'}</td>
                      <td className="small">
                        <div className="d-flex align-items-center flex-wrap gap-1">
                          {renderClaimBadges(user.claim)}
                        </div>
                      </td>
                      <td className="text-center">
                        {user.email_verified ? (
                          <i className="fas fa-check-circle text-success" title="Verified"></i>
                        ) : (
                          <i className="fas fa-times-circle text-danger" title="Not Verified"></i>
                        )}
                      </td>
                      <td className="text-center">
                        {user.disabled ? (
                          <span className="badge bg-danger">Disabled</span>
                        ) : (
                          <span className="badge bg-success">Active</span>
                        )}
                      </td>
                      <td className="small text-muted">
                        {formatDate(user.createdOn)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FirebaseUsers
